// FILE: frontend/src/pages/Credits.tsx
import { useEffect, useState } from "react";
import { Coins, RefreshCw, ShieldCheck, Sparkles, Video } from "lucide-react";
import toast from "react-hot-toast";

import { API_BASE_URL } from "@/lib/api-config";
import { auth } from "@/lib/firebase";
import { useCredits } from "@/context/CreditContext";
import { Link, useSearchParams } from "react-router-dom";

type CreditPackDetails = {
  credits: number;
  unit_amount: number;
  currency: string;
  formatted_price: string;
  video_render_cost: number;
  video_generations: number;
  credits_never_expire: boolean;
};

async function getAuthHeaders(): Promise<HeadersInit> {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  const isDev = import.meta.env.MODE === "development";
  if (isDev && import.meta.env.VITE_DEV_NO_AUTH === "true") {
    headers.Authorization = "Bearer dev";
    return headers;
  }

  const user = auth?.currentUser;
  if (user) {
    headers.Authorization = `Bearer ${await user.getIdToken()}`;
  }

  return headers;
}

export default function CreditsPage() {
  const {
    balance,
    isAdmin,
    packSize,
    videoCost,
    loading,
    error,
    refreshCredits,
  } = useCredits();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [packDetails, setPackDetails] = useState<CreditPackDetails | null>(
    null
  );
  const [packPriceLoading, setPackPriceLoading] = useState(true);
  const [packPriceError, setPackPriceError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const checkoutStatus = searchParams.get("checkout");

  useEffect(() => {
    if (isAdmin) {
      setPackPriceLoading(false);
      return;
    }

    let cancelled = false;

    const loadPackDetails = async () => {
      setPackPriceLoading(true);
      setPackPriceError(null);

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/payment/credit-pack`
        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            data?.detail?.message ||
              data?.message ||
              "Credit-pack pricing is temporarily unavailable."
          );
        }

        if (
          typeof data?.credits !== "number" ||
          typeof data?.formatted_price !== "string"
        ) {
          throw new Error("Credit-pack pricing response is invalid.");
        }

        if (!cancelled) {
          setPackDetails(data as CreditPackDetails);
        }
      } catch (priceError: any) {
        console.error("Failed to load credit-pack price:", priceError);

        if (!cancelled) {
          setPackPriceError(
            priceError?.message ||
              "Credit-pack pricing is temporarily unavailable."
          );
        }
      } finally {
        if (!cancelled) {
          setPackPriceLoading(false);
        }
      }
    };

    void loadPackDetails();

    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  useEffect(() => {
    if (checkoutStatus !== "success") {
      return;
    }

    const retryDelays = [0, 1000, 2500, 5000];
    const timers: number[] = [];

    for (const delay of retryDelays) {
      const timer = window.setTimeout(() => {
        void refreshCredits();
      }, delay);

      timers.push(timer);
    }

    const next = new URLSearchParams(searchParams);
    next.delete("session_id");
    setSearchParams(next, { replace: true });

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [checkoutStatus, refreshCredits, setSearchParams]);

  const startCheckout = async () => {
    setCheckoutLoading(true);

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL}/api/payment/create-checkout-session`,
        {
          method: "POST",
          headers,
          credentials: "include",
          body: JSON.stringify({}),
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data?.detail?.message ||
            data?.message ||
            "Checkout is not available yet."
        );
      }

      if (!data?.checkout_url) {
        throw new Error("Stripe Checkout URL was not returned.");
      }

      window.location.assign(data.checkout_url);
    } catch (checkoutError: any) {
      toast.error(
        checkoutError?.message ||
          "Unable to start checkout. Please try again later."
      );
    } finally {
      setCheckoutLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-8 px-4 py-8 text-slate-900 dark:text-slate-100">
      {checkoutStatus === "success" && (
        <div
          className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200"
          role="status"
        >
          Payment completed. Your credit balance is being refreshed.
        </div>
      )}

      {checkoutStatus === "cancelled" && (
        <div
          className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200"
          role="status"
        >
          Checkout was cancelled. You were not charged.
        </div>
      )}

      <div>
        <p className="mb-2 text-sm font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">
          AutoQuiz AI
        </p>
        <h1 className="text-4xl font-bold">Credits</h1>
        {isAdmin && (
          <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-sm font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
            <ShieldCheck size={16} />
            Admin testing account
          </div>
        )}
        <p className="mt-3 max-w-2xl text-slate-600 dark:text-slate-400">
          No subscriptions and no monthly reset. Use credits whenever you want
          to generate a video.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
          <div className="mb-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-blue-100 p-3 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300">
                <Coins size={24} />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Current balance
                </p>
                <p className="text-3xl font-bold">
                  {loading ? "…" : balance ?? "—"}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => void refreshCredits()}
              disabled={loading}
              className="rounded-lg border border-slate-200 p-2 text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700"
              aria-label="Refresh credits"
            >
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
            </button>
          </div>

          {error && (
            <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
              {error}
            </p>
          )}
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
          <div className="mb-4 flex items-center gap-3">
            <div className="rounded-xl bg-purple-100 p-3 text-purple-600 dark:bg-purple-900/30 dark:text-purple-300">
              <Video size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Video generation
              </p>
              <p className="text-2xl font-bold">{videoCost} credits</p>
            </div>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Quiz generation and previews are free. Credits are charged only
            when you generate the final video.
          </p>
        </section>
      </div>

      {!isAdmin && (
      <section className="overflow-hidden rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 to-purple-50 p-7 shadow-sm dark:border-blue-900/60 dark:from-blue-950/40 dark:to-purple-950/40">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-center">
          <div>
            <div className="mb-2 flex items-center gap-2">
              <Sparkles size={20} className="text-purple-600 dark:text-purple-400" />
              <span className="font-semibold text-purple-700 dark:text-purple-300">
                Credit pack
              </span>
            </div>
            <h2 className="text-3xl font-bold">
              {packDetails?.credits ?? packSize} credits
              {packDetails && (
                <span className="text-purple-700 dark:text-purple-300">
                  {" "}— {packDetails.formatted_price}
                </span>
              )}
            </h2>

            {packPriceLoading ? (
              <p className="mt-2 text-slate-600 dark:text-slate-400">
                Loading current price…
              </p>
            ) : packPriceError ? (
              <p
                className="mt-2 text-sm text-red-700 dark:text-red-300"
                role="alert"
              >
                {packPriceError}
              </p>
            ) : (
              <p className="mt-2 text-slate-600 dark:text-slate-400">
                One-time purchase
                {packDetails?.video_generations
                  ? ` · ${packDetails.video_generations} video generations`
                  : ""}
                {packDetails?.credits_never_expire
                  ? " · Credits never expire"
                  : ""}
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={() => void startCheckout()}
            disabled={
              checkoutLoading ||
              packPriceLoading ||
              !packDetails ||
              Boolean(packPriceError)
            }
            className="rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 px-7 py-3 font-semibold text-white shadow-lg shadow-blue-500/20 transition-transform hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {checkoutLoading
              ? "Opening checkout…"
              : packPriceLoading
                ? "Loading price…"
                : packDetails
                  ? `Buy ${packDetails.credits} Credits — ${packDetails.formatted_price}`
                  : "Purchase unavailable"}
          </button>
        </div>
      </section>
      )}

      {!isAdmin && (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          By purchasing credits, you agree to our{" "}
          <Link to="/terms" className="underline hover:no-underline">
            Terms of Service
          </Link>
          ,{" "}
          <Link to="/privacy" className="underline hover:no-underline">
            Privacy Policy
          </Link>
          , and{" "}
          <Link to="/refund-policy" className="underline hover:no-underline">
            Refund & Credit Policy
          </Link>
          .
        </p>
      )}

      <div className="grid gap-3 text-sm text-slate-600 dark:text-slate-400 sm:grid-cols-3">
        <div className="rounded-xl border border-slate-200 p-4 dark:border-slate-700">
          No subscriptions
        </div>
        <div className="rounded-xl border border-slate-200 p-4 dark:border-slate-700">
          Credits do not reset monthly
        </div>
        <div className="rounded-xl border border-slate-200 p-4 dark:border-slate-700">
          Failed renders are refunded
        </div>
      </div>
    </div>
  );
}
